import {
  ALLOWED_DATA_SOURCES,
  matchesPattern,
} from '@de/config/allowed-data-sources';
import type { InterpolatedRequest } from './fetchDataSource';

const OFFERINGS_SERVICE_GROUPS = ['offerings', 'offeringsService'] as const;

export function extractPath(endpoint: string): string {
  if (!endpoint) return '';
  if (endpoint.startsWith('/')) return endpoint.split('?')[0] ?? '';
  try {
    const parsed = new URL(endpoint);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    return parsed.pathname;
  } catch {
    return '';
  }
}

/**
 * True when the resolved request targets an Offerings allowlist entry that
 * accepts POST with a body (e.g. `/v1/offerings/search/match`).
 */
export function isOfferingsPostRequest(req: InterpolatedRequest): boolean {
  if (req.method !== 'POST') return false;
  const path = extractPath(req.endpoint);
  if (!path) return false;

  for (const group of OFFERINGS_SERVICE_GROUPS) {
    for (const def of ALLOWED_DATA_SOURCES[group]) {
      if (
        def.methods.some((m) => m === 'POST') &&
        matchesPattern(path, def.pattern)
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Auto-inject step-sourced `flowType` into Offerings POST bodies (SAD v4
 * Decision 5). Step-level value wins over any DSL template field so operators
 * cannot drop budget filtering via body edits.
 */
export function injectFlowTypeIntoOfferingsRequest(
  req: InterpolatedRequest,
  flowType: string | undefined,
): InterpolatedRequest {
  if (!flowType || !isOfferingsPostRequest(req)) {
    return req;
  }

  const body = req.body;
  if (body === undefined || body === null) {
    return { ...req, body: { flowType } };
  }
  if (typeof body !== 'object' || Array.isArray(body)) {
    return req;
  }

  return {
    ...req,
    body: { ...(body as Record<string, unknown>), flowType },
  };
}
